'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// UI lib components
import {
  Breadcrumbs,
  Box,
  Menu,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconDots, IconFolder } from '@tabler/icons-react';

// Types
import { ResourcePathItem, ResourcePathProps } from './types';

// Style
import * as classes from './styles/index.css';
import * as breadcrumbsStylesAPI from './styles/breadcrumbsStylesAPI.css';
import NavigationItem from './NavigationItem';
import {
  useGetResourcePathQuery,
  useUpdateResourceMutation,
} from '@app/types/blackroom-api';
import useWorkspace from '@app/features/Workspace/store/hooks/useWorkspace';
import { getResourcePathItemUrl } from './utils';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

const MAX_VISIBLE_ITEMS = 3;

function ResourcePath({
  parentId = null,
  scope = 'my',
  navigation = 'none',
  onNavigateToPathItem,
  withRootElement = false,
  showRootElementAtRoot = false,
  disableRootNavigation = false,
  currentFolderEditable = false,
}: ResourcePathProps) {
  /* ********************************** HOOKS ********************************* */

  // Localization
  const { t } = useTranslation('Resource');
  const workspace = useWorkspace();
  const [overflowMenuOpened, setOverflowMenuOpened] = useState(false);
  /* ***************************** LOCAL VARIABLES **************************** */

  const props = {
    parentId,
    scope,
    navigation,
    onNavigateToPathItem,
    withRootElement,
    showRootElementAtRoot,
    disableRootNavigation,
  };

  const { data } = useGetResourcePathQuery({
    variables: { resourceId: parentId ?? '', workspaceId: workspace.id },
    skip: parentId === null,
  });
  const [updateResource] = useUpdateResourceMutation();

  /* ***************************** RENDER HELPERS ***************************** */

  function isNavigationDisabled(item: ResourcePathItem) {
    if (item.id === 'root' && disableRootNavigation) {
      return true;
    }

    return navigation === 'none';
  }

  function getNavigationItem(
    item: ResourcePathItem,
    index: number,
    total: number,
  ) {
    return (
      <NavigationItem
        key={item.id ?? index}
        item={item}
        isCurrent={index === total - 1}
        onRenameCurrentItem={
          currentFolderEditable && parentId !== null
            ? renameCurrentFolder
            : undefined
        }
        {...props}
      />
    );
  }

  async function renameCurrentFolder(name: string) {
    if (parentId === null) return;

    try {
      await updateResource({
        variables: {
          updateResourceInput: {
            id: parentId,
            name,
          },
        },
      });
    } catch (error) {
      showNotification({
        title: t('Common:errors.genericTitle'),
        message: t('Common:errors.genericMessage'),
        color: 'blackroomRed',
      });
      throw error;
    }
  }

  function getOverflowMenu(hiddenItems: ResourcePathItem[]) {
    return (
      <Box key="overflow-menu" className={classes.overflowBreadcrumb}>
        <Menu
          position="bottom-start"
          radius="sm"
          shadow="lg"
          width={360}
          withinPortal
          opened={overflowMenuOpened}
          onChange={setOverflowMenuOpened}
        >
          <Menu.Target>
            <UnstyledButton
              className={classes.overflowButton}
              data-expanded={overflowMenuOpened || undefined}
            >
              <IconDots size={22} stroke={3} />
            </UnstyledButton>
          </Menu.Target>

          <Menu.Dropdown>
            {hiddenItems.map((item) => {
              const disabled = isNavigationDisabled(item);
              const label = (
                <Text className={classes.overflowMenuLabel} truncate>
                  {item.title}
                </Text>
              );

              return (
                <Tooltip
                  key={item.id ?? item.title}
                  label={item.title}
                  openDelay={500}
                  withinPortal
                >
                  {navigation === 'page' && !disabled ? (
                    <Menu.Item
                      component={Link}
                      leftSection={<IconFolder size={20} />}
                      to={getResourcePathItemUrl(item, workspace.id, scope)}
                    >
                      {label}
                    </Menu.Item>
                  ) : (
                    <Menu.Item
                      disabled={disabled}
                      leftSection={<IconFolder size={20} />}
                      onClick={
                        navigation === 'table'
                          ? () => onNavigateToPathItem?.(item.id)
                          : undefined
                      }
                    >
                      {label}
                    </Menu.Item>
                  )}
                </Tooltip>
              );
            })}
          </Menu.Dropdown>
        </Menu>
      </Box>
    );
  }

  function getPathItems() {
    const path = data?.resource.path ?? [];

    const initialItems =
      withRootElement || (path.length > 0 && showRootElementAtRoot)
        ? [
            {
              id: 'root',
              title: t(`PageResources.resourcePath.scope.${scope}`),
            },
          ]
        : [];

    const allItems = [
      ...initialItems,
      ...path.map((resource) => ({
        id: resource.id,
        title: resource.name,
      })),
    ];

    if (allItems.length > MAX_VISIBLE_ITEMS) {
      const lastItems = allItems.slice(-(MAX_VISIBLE_ITEMS - 1));
      const hiddenItems = allItems.slice(1, -(MAX_VISIBLE_ITEMS - 1));

      return [
        getNavigationItem(allItems[0], 0, allItems.length),
        getOverflowMenu(hiddenItems),
        ...lastItems.map((item, i) => {
          const originalIndex = allItems.length - (MAX_VISIBLE_ITEMS - 1) + i;
          return getNavigationItem(item, originalIndex, allItems.length);
        }),
      ];
    }

    return allItems.map((item, index) =>
      getNavigationItem(item, index, allItems.length),
    );
  }

  /* ******************************** RENDERING ******************************* */

  return (
    <Breadcrumbs
      className={classes.resourcePath}
      classNames={{
        ...breadcrumbsStylesAPI,
        breadcrumb: classes.breadcrumb,
      }}
      separator=">"
    >
      {getPathItems()}
    </Breadcrumbs>
  );
}

export default ResourcePath;
